import React, { useState } from 'react';
import {
  HStack,
  Button,
  Textarea,
  VStack,
  Box,
  Icon,
  Drawer,
  Portal,
  CloseButton,
} from '@chakra-ui/react';
import { StyledButton } from '@/components/ui/styled-button';
import { FiMessageCircle, FiThumbsDown, FiThumbsUp } from 'react-icons/fi';
import { useMessageUpdate } from '@/features/messages/messages.hook';
import { Message } from '@/features/messages/messages.types';

interface MessageFeedbackProps {
  message: Message;
}

export const MessageFeedback: React.FC<MessageFeedbackProps> = ({
  message,
}) => {
  const [comment, setComment] = useState(message?.comments || '');

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState<
    'positive' | 'negative' | null
  >(
    message?.feedback_positive
      ? 'positive'
      : message?.feedback_negative
        ? 'negative'
        : null,
  );

  // Use the feedback hook with mutation
  const {
    mutate: updateMessage,
    isPending,
    data: updatedMessage,
  } = useMessageUpdate();

  // TODO: Commented for testing the feature
  // if (!message.from_agent) return null;

  const handleQuickFeedback = (type: 'positive' | 'negative') => {
    setFeedbackGiven(type);
    // Submit feedback immediately for quick actions
    updateMessage({
      id: message.id,
      data: {
        ...message,
        // TODO: Uncomment this when the backend is updated
        feedback_positive: type === 'positive' ? true : null,
        feedback_negative: type === 'negative' ? true : null,
      },
      previousData: message,
    });
  };

  const handleCommentSubmit = async (type: 'positive' | 'negative') => {
    // Submit feedback with comment
    updateMessage({
      id: message.id,
      data: {
        ...message,
        comments: comment,
        feedback_positive: type === 'positive' ? true : null,
        feedback_negative: type === 'negative' ? true : null,
      },
      previousData: message,
    });
    setFeedbackGiven(type);
    setIsDrawerOpen(false);
  };

  const isCommented = message?.comments || updatedMessage?.data?.comments;

  return (
    <VStack align="stretch" mt={2}>
      {/* Subtle feedback icons */}
      <HStack opacity={0.6} _hover={{ opacity: 1 }} transition="opacity 0.2s">
        <Button
          aria-label="Good response"
          size="sm"
          variant="ghost"
          color={feedbackGiven === 'positive' ? 'green.500' : 'gray.500'}
          _hover={{ bg: 'gray.100', color: 'green.500' }}
          onClick={() => handleQuickFeedback('positive')}
          minW="24px"
          h="24px"
          p={0}
          mr={1}
        >
          <Icon as={FiThumbsUp} />
        </Button>

        <Button
          aria-label="Poor response"
          size="sm"
          variant="ghost"
          color={feedbackGiven === 'negative' ? 'red.500' : 'gray.500'}
          _hover={{ bg: 'gray.100', color: 'red.500' }}
          onClick={() => handleQuickFeedback('negative')}
          minW="24px"
          h="24px"
          p={0}
          mr={1}
        >
          <Icon as={FiThumbsDown} />
        </Button>

        {/* Drawer for comments */}
        <Drawer.Root
          placement="end"
          size="md"
          open={isDrawerOpen}
          onOpenChange={(open) => {
            setIsDrawerOpen(open.open);
          }}
        >
          <Drawer.Trigger asChild>
            <Button
              aria-label="Add comment"
              size="sm"
              variant="ghost"
              color={isCommented ? 'blue.500' : 'gray.500'}
              _hover={{ bg: 'gray.100', color: 'blue.500' }}
              minW="24px"
              h="24px"
              p={0}
              position="relative"
            >
              <Icon as={FiMessageCircle} />
              {isCommented && (
                <Box
                  position="absolute"
                  top="-2px"
                  right="-2px"
                  w="8px"
                  h="8px"
                  bg="blue.500"
                  borderRadius="full"
                  border="1px solid white"
                />
              )}
            </Button>
          </Drawer.Trigger>

          <Portal>
            <Drawer.Backdrop />
            <Drawer.Positioner>
              <Drawer.Content>
                <Drawer.Header
                  borderBottom="1px solid"
                  borderColor="gray.200"
                  pb={4}
                >
                  <Drawer.Title fontSize="xl" fontWeight="600" color="gray.800">
                    Message Feedback
                  </Drawer.Title>
                </Drawer.Header>

                <Drawer.Body p={6}>
                  <VStack align="stretch" gap={6}>
                    {/* Feedback Type Selection */}
                    <Box>
                      <Box
                        mb={4}
                        fontSize="md"
                        fontWeight="500"
                        color="gray.700"
                      >
                        How would you rate this response?
                      </Box>
                      <HStack gap={3} justify="start" w="100%" align="center">
                        <Button
                          size="md"
                          h="40px"
                          px={4}
                          bg={
                            feedbackGiven === 'positive'
                              ? 'green.50'
                              : 'transparent'
                          }
                          borderColor={
                            feedbackGiven === 'positive'
                              ? 'green.400'
                              : 'gray.300'
                          }
                          border="1px solid"
                          color={
                            feedbackGiven === 'positive'
                              ? 'green.600'
                              : 'gray.600'
                          }
                          onClick={() => setFeedbackGiven('positive')}
                          borderRadius="8px"
                          fontSize="sm"
                          fontWeight="500"
                          _hover={{
                            bg:
                              feedbackGiven === 'positive'
                                ? 'green.100'
                                : 'green.50',
                            borderColor: 'green.400',
                            color: 'green.600',
                          }}
                          transition="all 0.2s ease"
                        >
                          <Icon as={FiThumbsUp} mr={2} boxSize="16px" />
                          Good
                        </Button>
                        <Button
                          size="md"
                          h="40px"
                          px={4}
                          bg={
                            feedbackGiven === 'negative'
                              ? 'red.50'
                              : 'transparent'
                          }
                          borderColor={
                            feedbackGiven === 'negative'
                              ? 'red.400'
                              : 'gray.300'
                          }
                          border="1px solid"
                          color={
                            feedbackGiven === 'negative'
                              ? 'red.600'
                              : 'gray.600'
                          }
                          onClick={() => setFeedbackGiven('negative')}
                          borderRadius="8px"
                          fontSize="sm"
                          fontWeight="500"
                          _hover={{
                            bg:
                              feedbackGiven === 'negative'
                                ? 'red.100'
                                : 'red.50',
                            borderColor: 'red.400',
                            color: 'red.600',
                          }}
                          transition="all 0.2s ease"
                        >
                          <Icon as={FiThumbsDown} mr={2} boxSize="16px" />
                          Poor
                        </Button>
                      </HStack>
                    </Box>

                    {/* Comment Section */}
                    <Box>
                      <Box
                        mb={3}
                        fontSize="md"
                        fontWeight="500"
                        color="gray.700"
                      >
                        Additional feedback
                      </Box>
                      <Box mb={2} fontSize="sm" color="gray.500">
                        Help us improve by sharing more details about your
                        experience
                      </Box>
                      <Textarea
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Type your feedback here..."
                        rows={4}
                        resize="vertical"
                        borderRadius="12px"
                        border="2px solid"
                        borderColor="gray.200"
                        _focus={{
                          borderColor: 'blue.400',
                          boxShadow: '0 0 0 1px blue.400',
                        }}
                        _hover={{
                          borderColor: 'gray.300',
                        }}
                        fontSize="md"
                        p={4}
                      />
                    </Box>
                  </VStack>
                </Drawer.Body>

                <Drawer.Footer
                  borderTop="1px solid"
                  borderColor="gray.200"
                  pt={4}
                  pb={6}
                  px={6}
                >
                  <HStack gap={3} width="100%" justify="flex-end">
                    <Drawer.CloseTrigger />

                    <StyledButton
                      variant="primary"
                      size="lg"
                      px={8}
                      onClick={() => {
                        if (feedbackGiven) {
                          handleCommentSubmit(feedbackGiven);
                        } else {
                          console.log('Please select a feedback type first');
                        }
                      }}
                      disabled={!feedbackGiven || isPending}
                      loading={isPending}
                      loadingText="Submitting..."
                    >
                      Submit Feedback
                    </StyledButton>
                  </HStack>
                </Drawer.Footer>

                <Drawer.CloseTrigger asChild>
                  <CloseButton
                    size="md"
                    position="absolute"
                    top="4"
                    right="4"
                    color="gray.500"
                    _hover={{
                      bg: 'gray.100',
                      color: 'gray.700',
                    }}
                    borderRadius="8px"
                  />
                </Drawer.CloseTrigger>
              </Drawer.Content>
            </Drawer.Positioner>
          </Portal>
        </Drawer.Root>
      </HStack>
    </VStack>
  );
};
